import { Screen, AllScreensQueryResult } from "src/types.g";

export enum ScreenCapabilityEnum {
  RELOAD = "reload",
  REFRESH = "refresh",
  SCREENSHOT = "screenshot",
  CLEAR_CACHE = "clear_cache",
  REBOOT = "reboot",
  RESTART = "restart",
  CAST = "cast",
  TRANSFER = "transfer",
  MUTE = "mute",
}

export type ScreenCapability = `${ScreenCapabilityEnum}`;

/**
 * Helper function to check if a screen has a specific capability
 *
 * @param screen The screen object to check
 * @param capability The capability to check for
 * @returns boolean indicating if the screen has the capability
 */
export const hasScreenCapability = (
  screen: Screen,
  capability: ScreenCapability,
): boolean => {
  if (!screen) return false;

  // Some capabilities have platform-specific checks
  switch (capability) {
    case ScreenCapabilityEnum.CLEAR_CACHE:
      return (
        screen.devicePlatform?.toLowerCase() === "android" ||
        screen.devicePlatform?.toLowerCase() === "amazon" ||
        screen.devicePlatform?.toLowerCase() === "amazonsignage" ||
        screen.deviceInfo?.native_player?.capabilities?.clear_cache ||
        screen.deviceInfo?.native_player?.screen_api_command?.clear_cache
      );
    case ScreenCapabilityEnum.REFRESH:
    case ScreenCapabilityEnum.RELOAD:
      return true; // the reload and refresh should be supported by all devices/platforms
    case ScreenCapabilityEnum.RESTART:
      return (
        screen.deviceInfo?.native_player?.capabilities?.restart ||
        screen.deviceInfo?.native_player?.screen_api_command?.restart
      );
    case ScreenCapabilityEnum.REBOOT:
      return (
        screen.deviceInfo?.native_player?.capabilities?.reboot ||
        screen.deviceInfo?.native_player?.screen_api_command?.reboot
      );
    case ScreenCapabilityEnum.MUTE:
      return (
        screen.deviceInfo?.native_player?.capabilities?.mute &&
        screen.deviceInfo?.native_player?.screen_api_command?.mute
      );
    // For other capabilities, check if they exist in both capabilities and screen_api_command
    default:
      return !!(
        screen.deviceInfo?.native_player?.capabilities?.[capability] ||
        screen.deviceInfo?.native_player?.screen_api_command?.[capability]
      );
  }
};

/**
 * Helper function to get compatible platforms for bulk actions
 *
 * @param selectedScreens Set of selected screen IDs
 * @param screens Array of screen objects
 * @param capability The capability to check for
 * @returns Array of platform objects with compatibility info
 */
export const getCompatiblePlatforms = (
  selectedScreens: Set<string>,
  screens: NonNullable<
    NonNullable<AllScreensQueryResult["data"]>["allScreens"]
  >["nodes"],
  capability: ScreenCapability,
) => {
  return Array.from(selectedScreens).map((screenId) => {
    const screen = screens.find((s) => s.id === screenId);
    return {
      id: screenId,
      name: screen?.name || "Unknown Screen",
      platform: screen?.devicePlatform || "unknown",
      compatible: screen
        ? hasScreenCapability(screen as Screen, capability)
        : false,
    };
  });
};
